// UI orchestrator. Owns the panels, exposes a thin API for the App to drive them.
// Heavy DOM stays inside the per-panel modules; this file just wires them together.

import type { Room, RoomsConfig, Variant } from '../app/types';
import { Topbar } from './topbar';
import { VariantPanel } from './variantPanel';
import { Floorplan } from './floorplan';

export interface UiHandlers {
  onVariantSelect: (variant: Variant) => void;
  onRoomSelect: (room: Room) => void;
  onBeforeAfterToggle: () => void;
}

export class Ui {
  private readonly topbar: Topbar;
  private readonly variantPanel: VariantPanel;
  readonly floorplan: Floorplan;
  private readonly bottombar: HTMLElement;
  private readonly beforeAfterButton: HTMLButtonElement;
  private handlers: UiHandlers;

  constructor() {
    this.topbar = new Topbar();
    this.variantPanel = new VariantPanel();
    this.floorplan = new Floorplan();
    const bottom = document.getElementById('bottombar');
    const ba = document.getElementById('beforeafter-button') as HTMLButtonElement | null;
    if (!bottom || !ba) throw new Error('[ui] missing #bottombar or #beforeafter-button');
    this.bottombar = bottom;
    this.beforeAfterButton = ba;
    this.beforeAfterButton.addEventListener('click', () => this.handlers.onBeforeAfterToggle());
    this.handlers = noopHandlers();
  }

  setHandlers(handlers: UiHandlers): void {
    this.handlers = handlers;
    this.variantPanel.onSelect((v) => this.handlers.onVariantSelect(v));
    this.floorplan.onSelect((r) => this.handlers.onRoomSelect(r));
  }

  initialize(config: RoomsConfig): void {
    this.floorplan.setRooms(config.rooms, config.defaultRoomId, config.features ?? []);
    applyBrandWatermark(config.branding);
    this.topbar.show();
    this.bottombar.classList.remove('hidden');
  }

  setRoom(room: Room, currentVariantId: string): void {
    this.topbar.setRoom(room);
    this.variantPanel.setRoom(room, currentVariantId);
    this.floorplan.setCurrentRoom(room.id);
    this.beforeAfterButton.classList.toggle('hidden', !room.beforeVariantId);
  }

  setActiveVariant(variantId: string, isShowingBefore: boolean): void {
    this.variantPanel.setActiveVariant(variantId);
    this.beforeAfterButton.classList.toggle('is-active', isShowingBefore);
  }
}

function noopHandlers(): UiHandlers {
  return {
    onVariantSelect: () => {},
    onRoomSelect: () => {},
    onBeforeAfterToggle: () => {},
  };
}

// Pushes branding.logoUrl into the persistent bottom-left watermark <img>.
// CSS hides .brand-watermark until [src] is set, so projects without a logo
// simply skip rendering it.
function applyBrandWatermark(branding: { logoUrl?: string; name?: string }): void {
  const img = document.getElementById('brand-watermark') as HTMLImageElement | null;
  if (!img) return;
  if (branding.logoUrl) {
    img.src = branding.logoUrl;
    img.alt = branding.name || '';
  } else {
    img.removeAttribute('src');
  }
}
